const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [role="combobox"]:not([tabindex="-1"]), [role="textbox"]:not([tabindex="-1"]), [role="button"]:not([tabindex="-1"])';

const isElementVisible = element => {
  const style = window.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    element.offsetParent !== null
  );
};

const getFocusableElements = container => {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    isElementVisible
  );
};

const focusButton = (buttonRef) => {
  buttonRef.current?.focus();
  buttonRef.current?.setAttribute("tabIndex", "0");
};

const focusUploadButton = (uploadButtonRef) => {
  setTimeout(() => {
    if (uploadButtonRef?.current) {
      uploadButtonRef.current.focus();
    }
  }, 100);
};

const navigateFromUploadToDocuments = (
  activeElement,
  e,
  uploadButtonRef
) => {
  const documentsHeader = document.querySelector(".documents-header");
  if (!documentsHeader || !documentsHeader.contains(activeElement) || e.shiftKey) return;

  if (activeElement.id === "upload-document-button") {
    e.preventDefault();
    const firstDocument = document.querySelector(".documents-list [role='listitem']");
    if (firstDocument) {
      const firstFocusableElement = firstDocument.querySelector(FOCUSABLE_SELECTOR);
      firstFocusableElement?.focus();
    }
  }
};

const navigateFromDocumentsToUpload = (
  activeElement,
  e,
  uploadButtonRef
) => {
  const documentsList = document.querySelector(".documents-list");
  if (!documentsList || !documentsList.contains(activeElement)) return;

  const allDocuments = documentsList.querySelectorAll("[role='listitem']");
  const lastDocument = allDocuments[allDocuments.length - 1];
  
  if (lastDocument && lastDocument.contains(activeElement)) {
    const focusableElements = getFocusableElements(lastDocument);
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    
    if (activeElement === lastFocusableElement && !e.shiftKey) {
      e.preventDefault();
      focusButton(uploadButtonRef);
    }
  }
};

const navigateWithinDocument = (
  activeElement,
  e,
  uploadButtonRef
) => {
  const listItem = activeElement.closest("[role='listitem']");
  if (!listItem) return

  const focusableElements = getFocusableElements(listItem);
  const currentIndex = focusableElements.indexOf(activeElement);
  
  if (currentIndex < 0) return;

  const currentIndexIsLastElement = currentIndex === focusableElements.length - 1
  if (currentIndexIsLastElement && !e.shiftKey) {
    e.preventDefault(); 

    const allDocuments = document.querySelectorAll(".documents-list [role='listitem']");
    const currentDocumentIndex = Array.from(allDocuments).indexOf(listItem);
    
    const hasNextDocument = currentDocumentIndex < allDocuments.length - 1;
    if (hasNextDocument) {
      const nextDocument = allDocuments[currentDocumentIndex + 1];
      const firstFocusableElement = nextDocument.querySelector(FOCUSABLE_SELECTOR);

      firstFocusableElement?.focus();
    } else {
      // dev: if its the first element, focus upload button
      focusButton(uploadButtonRef);
    }
  }

  const currentIndexIsFirstElement = currentIndex === 0;
  if (currentIndexIsFirstElement && e.shiftKey) {
    e.preventDefault();
    
    const allDocuments = document.querySelectorAll(".documents-list [role='listitem']");
    const currentDocumentIndex = Array.from(allDocuments).indexOf(listItem);
    
    const hasPrevDocument = currentDocumentIndex > 0 
    if (hasPrevDocument) {
      const prevDocument = allDocuments[currentDocumentIndex - 1];
      const focusableElements = getFocusableElements(prevDocument);
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      lastFocusableElement?.focus();
    } else {
      // dev: if its the first element, focus upload button
      focusButton(uploadButtonRef);
    }
  }
};

export const createKeyboardNavigation = ({
  uploadButtonRef
}) => {
  const handleKeyDown = event => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "ArrowRight" || event.key === "ArrowLeft") {
      const documentsList = document.querySelector(".documents-list");
      if (!documentsList) return;
      
      const currentDocument = document.activeElement.closest("[role='listitem']");
      if (!currentDocument) return; 
      
      event.preventDefault();
      const allDocuments = document.querySelectorAll(".documents-list [role='listitem']");
      const currentIndex = Array.from(allDocuments).indexOf(currentDocument);
      
      const isGoingNext = event.key === "ArrowDown" || event.key === "ArrowRight";
      const hasNextDocument = currentIndex < allDocuments.length - 1;
      
      if (isGoingNext && hasNextDocument) {
        const nextDocument = allDocuments[currentIndex + 1];
        const firstFocusableElement = nextDocument.querySelector(FOCUSABLE_SELECTOR);
        firstFocusableElement?.focus();
      }
      
      const isGoingPrev = event.key === "ArrowUp" || event.key === "ArrowLeft";
      const hasPrevDocument = currentIndex > 0;
      
      if (isGoingPrev && hasPrevDocument) {
        const prevDocument = allDocuments[currentIndex - 1];
        const firstFocusableElement = prevDocument.querySelector(FOCUSABLE_SELECTOR);
        firstFocusableElement?.focus();
      }
    }
  };

  const handleTabKeyNavigation = e => {
    if (e.key !== "Tab") return;

    const activeElement = document.activeElement;

    navigateFromUploadToDocuments(
      activeElement,
      e,
      uploadButtonRef
    );
    
    navigateFromDocumentsToUpload(
      activeElement,
      e,
      uploadButtonRef
    );
    
    navigateWithinDocument(
      activeElement,
      e,
      uploadButtonRef
    );
  };

  const setupTabNavigation = () => {
    document.addEventListener("keydown", handleTabKeyNavigation);

    const handleFocusOut = e => {
      const documentsHeader = document.querySelector(".documents-header");
      if (documentsHeader && !documentsHeader.contains(e.relatedTarget)) {
        const uploadButton = documentsHeader.querySelector("#upload-document-button");
        if (uploadButton) {
          uploadButton.setAttribute("tabIndex", "-1");
        }
      }
    };

    document.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("keydown", handleTabKeyNavigation);
      document.removeEventListener("focusout", handleFocusOut);
    };
  };

  return {
    handleKeyDown,
    handleTabKeyNavigation,
    setupTabNavigation,
    focusUploadButton
  };
};
